/**
 * Turning a contact entry from `ListContacts` into a row on the contacts screen.
 *
 * Shared 1:1 with iOS `native-apps/ios/Symphony/Store/ContactRows.swift` and Android
 * `native-apps/android/app/src/main/java/com/wix/symphony/ContactRows.kt`. The rules below are a
 * cross-platform contract; change one and change all three.
 *
 * The server decides everything that depends on data the client cannot see: whether a thread is
 * waiting on the viewer, whether a person needs attention, which open loop to surface, and when the
 * last activity was. What is left here is presentation that three platforms would otherwise each
 * invent — initials, the shortened relative time on a row, and the two sections the list is split
 * into.
 *
 * The chips are client-side on purpose. The endpoint takes no filter: "waiting on me" is derived
 * from a message's direction and is not filterable on the underlying store, so a server-side chip
 * could only trim the page it had already fetched and would report "none" for a person whose
 * waiting thread sat outside it. Every entry already carries `waitingOnMe` and `needsAttention`, so
 * filtering here is both correct and free.
 */

/** The subset of a `ContactCard` these rules read. */
export type ContactRowCard = {
  personId?: string;
  displayName?: string;
  labels?: string[];
  lastTouchAt?: string | Date;
  needsAttention?: boolean;
  attentionSummary?: string;
  openLoopCount?: number;
};

/** The subset of a `ContactWithOutreaches` these rules read. */
export type ContactRowEntry = {
  contact?: ContactRowCard;
  lastActivityAt?: string | Date;
  waitingOnMe?: boolean;
  waitingCount?: number;
};

/**
 * Which chip the list is filtered by.
 *
 * There is ONE attention chip, and it is the same rule as the "Needs attention" SECTION below.
 * They used to be two — a `needs-attention` that read only the open loops and a `waiting` that read
 * only `waitingOnMe` — while the section of that name was the union. So the chip labelled "Needs
 * attention" selected strictly fewer people than the section labelled "Needs attention", on the same
 * screen. The two questions are still distinguishable per row, which is where they are legible:
 * `rowSubtitle` says "waiting on your reply" or names the loop.
 */
export type ContactChip = 'all' | 'new' | 'action-required' | 'needs-attention';

/**
 * Whether a person has NEW correspondence on this device: their last activity is later than the device's
 * seen-mark for them (`lib/contact-seen.ts`), or — never opened here — within the last day. Pure; the
 * same rule on all three surfaces, each with its own per-device mark.
 */
export function isNewContact(entry: { lastActivityAt?: string | Date }, seenAt: number | null, now: number): boolean {
  if (!entry.lastActivityAt) return false;
  const at = new Date(entry.lastActivityAt).getTime();
  if (!Number.isFinite(at)) return false;
  return at > (seenAt ?? now - 24 * 60 * 60 * 1000);
}

// Initials are deliberately NOT here. `lib/avatar-color.ts` already owns them app-wide — first letter
// of the first two words — and its own comment records that iOS and Android mirror it. A second rule
// on this screen would give the same person one monogram in chat and a different one in contacts.

/**
 * The short relative time a row shows — "2h", "3d", "5w".
 *
 * Deliberately terse and never "now": the row is scanned, not read, and a column that changes width
 * between "just now" and "2d" makes the list jitter. Anything under an hour is "now" only in the
 * long form below; here it collapses to minutes so the column stays two or three characters.
 *
 * Returns an empty string when there is no timestamp, which is a real state — a person nobody has
 * spoken to yet — and must render as blank rather than as the epoch.
 */
export function shortRelativeTime(at: string | Date | undefined, now: Date = new Date()): string {
  const then = toDate(at);
  if (!then) return '';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return `${Math.max(1, seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `${weeks}w`;
  return `${Math.floor(days / 365)}y`;
}

/**
 * The long form the detail screen shows against "Last contacted" — "2 days ago".
 *
 * Separate from the short form rather than derived from it: the row needs a column that does not
 * move, and the detail row is prose. Sharing one function would force one of them to read wrong.
 */
export function longRelativeTime(at: string | Date | undefined, now: Date = new Date()): string {
  const then = toDate(at);
  if (!then) return '';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const units: [number, string][] = [
    [60, 'minute'],
    [60, 'hour'],
    [24, 'day'],
    [7, 'week'],
  ];
  let value = seconds;
  let label = 'second';
  for (const [size, name] of units) {
    if (value < size) break;
    value = Math.floor(value / size);
    label = name;
  }
  return `${value} ${label}${value === 1 ? '' : 's'} ago`;
}

/**
 * Whether an entry belongs under the given chip.
 *
 * The attention chip is `needsAttention` itself, so the chip and the section it shares a name with
 * can never disagree about who belongs in it.
 */
export function matchesChip(entry: ContactRowEntry, chip: ContactChip, isNew?: (entry: ContactRowEntry) => boolean): boolean {
  if (chip === 'needs-attention' || chip === 'action-required') return needsAttention(entry);
  if (chip === 'new') return isNew ? isNew(entry) : false;
  return true;
}

/**
 * The two sections the design splits the list into.
 *
 * "Needs Attention" is anyone the ball is with OR who has an open loop — the two are different
 * questions ("they are waiting on a reply" vs "I promised something") and the screen shows one
 * section for both, so a person in either belongs there. Everyone else falls to "All Contacts",
 * and nobody appears twice.
 */
export function sectionsOf<T extends ContactRowEntry>(entries: T[]): { needsAttention: T[]; all: T[] } {
  // Generic so a caller holding a RICHER row keeps its own type through the split — the docs above
  // already promised that of `needsAttention`, and narrowing to `ContactRowEntry` here forced every
  // caller to cast the result back to the thing it passed in.
  const attention: T[] = [];
  const all: T[] = [];
  for (const entry of entries) {
    if (needsAttention(entry)) attention.push(entry);
    else all.push(entry);
  }
  return { needsAttention: attention, all };
}

/**
 * Whether this entry belongs in the "Needs Attention" section.
 *
 * Exported as its own predicate so a caller holding a richer row than `ContactRowEntry` can
 * partition its own list with the same rule, instead of matching results back by identity.
 */
export function needsAttention(entry: ContactRowEntry): boolean {
  // ONLY the People service's verdict (an open loop / a question left hanging). `waitingOnMe` used to
  // count too, and it fires whenever the last message on a thread is the contact's — so a "confirmed,
  // see you tomorrow" and a "17 purchases so far" both wore "Action required" (a real report: twelve
  // rows flagged, one of them an actual ask). A reply that asks nothing is not an action.
  return Boolean(entry.contact?.needsAttention);
}

/** A reply came in and nobody has answered it — informational, not an action: the row says "Replied". */
export function repliedUnanswered(entry: ContactRowEntry): boolean {
  return Boolean(entry.waitingOnMe) && !needsAttention(entry);
}

/**
 * The subtitle under a name on a list row.
 *
 * Prefers what the person is waiting on the viewer for, because that is the actionable half; falls
 * back to the open loop the server picked. Empty when neither applies, and the row then shows the
 * name alone rather than a blank line.
 */
export function rowSubtitle(entry: ContactRowEntry): string {
  return entry.contact?.attentionSummary ?? '';
}

/**
 * The i18n key for the "waiting on you" subtitle, or null when the row has none.
 *
 * Split out because the subtitle is TWO different things: a fixed phrase we own and must translate,
 * and the server's own open-loop summary, which is already the user's language and must be shown
 * verbatim. Returning one string for both meant the fixed half shipped as hardcoded English — it
 * rendered in English in all 36 non-English bundles even though the key existed. Same shape as
 * `sourceLabelKey`: a key or null, never a sentence.
 */
export function rowSubtitleKey(entry: ContactRowEntry): string | null {
  return entry.waitingOnMe ? 'contacts.row.waitingOnYou' : null;
}

/**
 * The i18n key naming where a contact came from, or null when there is nothing to translate.
 *
 * `Person.source` is free text (`StringValue`, maxLength 40), NOT an enum — the four values below
 * are just the ones anything writes today (`phone` from the contacts import, `outreach` and
 * `calendar` from their consumers, `agent`). So a caller must render the RAW value when this
 * returns null: a closed map would blank out the row the day a fifth source appears, and this field
 * is one string literal in one service away from having one.
 */
export function sourceLabelKey(source: string | undefined): string | null {
  const known = ['phone', 'outreach', 'calendar', 'agent'];
  const value = (source ?? '').trim().toLowerCase();
  return known.includes(value) ? `contacts.detail.source.${value}` : null;
}

/**
 * A message with the outreach envelope's `Message from <name>: ` prefix removed.
 *
 * Every outreach body in production opens with it — it is addressed to the RECIPIENT, whose client
 * needs telling who is writing. On this screen the reader already knows: they are looking at that
 * person's own page, and each bubble is already on its speaker's side. So the prefix is pure noise
 * that costs a preview row most of its width.
 *
 * Deliberately narrow: it strips only that exact envelope, at the very start, with a short name.
 * Anything else is returned untouched — a body that merely contains a colon must not be truncated.
 */
export function strippedMessageText(text: string | undefined): string {
  const value = (text ?? '').trim();
  // BOTH forms occur, and roughly half the corpus is `Reply from`. Matching only `Message from`
  // stripped outbound turns and left every reply wearing its envelope.
  const match = /^(?:Message|Reply) from [^:\n]{1,100}:\s*/.exec(value);
  return match ? value.slice(match[0].length).trim() : value;
}

/**
 * The line under a thread row's date — the latest thing said on it.
 *
 * Secondary, because it cannot identify the thread: an outreach has no subject and one cannot be
 * derived (in production every body opens `Hi,` followed by the same "I'm <agent>, <owner>'s AI
 * assistant" boilerplate, so any first-line heuristic yields a list of identical rows). The row is
 * titled by its creation date instead — see `threadDateParts`.
 */
export function threadPreview(thread: { lastMessage?: string }): string {
  return strippedMessageText(thread.lastMessage);
}

/**
 * How a thread row titles itself: its real subject when it has one, else the date it was opened.
 *
 * The subject is what the assistant wrote into the message metadata when it composed the outreach
 * (`smsSubject`/`emailSubject`), served by the BFF. It is often ABSENT — the in-app channel writes
 * none — so the date fallback is the normal case, not an error path, and both have to read well.
 *
 * Empty only when there is neither, which is why the caller can render this directly.
 */
export function threadTitle(thread: { subject?: string; createdAt?: string | Date }, locale?: string): string {
  const subject = (thread.subject ?? '').trim();
  return subject || threadDateLabel(thread.createdAt, locale);
}

/**
 * The date a thread was opened, as the row's fallback title.
 *
 * Absolute, and carrying the TIME as well as the day, because it stands in for a subject and so
 * has to tell two rows apart. Several of the same person's outreaches routinely share a date — three
 * on one day in the data this was built against — and a relative label ("5w") collapses them
 * completely, which is the same failure as the boilerplate first line.
 *
 * Empty when there is no date, so a caller shows nothing rather than an epoch. iOS and Android
 * format the same choice with their own locale APIs (`DateFormatter` / `DateFormat`).
 */
export function threadDateLabel(at: string | Date | undefined, locale?: string): string {
  const date = toDate(at);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Whether a message opens a new run by its speaker — i.e. whether to draw a sender header on it.
 *
 * The chat idiom: consecutive messages from one side show the name once, on the first of the run.
 * Repeating it on every bubble is what makes a two-party thread read as a form rather than a
 * conversation. `index === 0` always opens a run.
 *
 * Who the speaker IS is not carried per message and does not need to be: an outreach has exactly two
 * ends, so `fromMe` plus the contact's own name names both. The server resolves `fromMe` against the
 * caller's side (the same message is FROM_SOURCE on both ends and means opposite things), so the
 * clients never re-derive it.
 */
export function startsSenderRun(messages: ThreadSpeaker[], index: number): boolean {
  if (index <= 0) return true;
  return speakerOf(messages[index]) !== speakerOf(messages[index - 1]);
}

/**
 * The channels an outreach can go out on, in the order the palette assigns them a colour.
 *
 * `channel` is a lowercased free string on the wire, not an enum — the four below are what anything
 * writes today. An unknown one is NOT dropped: it takes the neutral slot and still renders, the same
 * rule `sourceLabelKey` follows, because a fifth channel is one string literal away.
 */
export const CHANNEL_SLOTS = ['whatsapp', 'email', 'app', 'sms', 'gmail_connector'] as const;

export type ChannelSlot = (typeof CHANNEL_SLOTS)[number] | 'other';

/**
 * Which colour a channel wears. One slot per channel so the same channel is the same colour on every
 * surface and on every row — the point is that a thread's medium is recognisable at a glance, which
 * a single grey label could never do.
 *
 * Returns a SLOT, never a colour: the token layer resolves it, so dark mode and any future re-export
 * move all three surfaces at once. Shared 1:1 with the native `channelSlot`.
 */
export function channelSlot(channel: string | undefined): ChannelSlot {
  const value = (channel ?? '').trim().toLowerCase();
  return (CHANNEL_SLOTS as readonly string[]).includes(value) ? (value as ChannelSlot) : 'other';
}

/**
 * The label a channel shows.
 *
 * A known channel is translated; an unknown one renders its RAW value rather than being blanked,
 * for the same reason `sourceLabelKey` returns null instead of a default.
 */
export function channelLabelKey(channel: string | undefined): string | null {
  const slot = channelSlot(channel);
  return slot === 'other' ? null : `contacts.channel.${slot}`;
}

/** The subset of a message the speaker rules read. */
export type ThreadSpeaker = { fromMe?: boolean; senderName?: string };

/**
 * A stable identity for whoever wrote a message, used only to compare two of them.
 *
 * Prefers the envelope name over `fromMe`, because `fromMe` is WRONG on older messages: they carry
 * no direction, so the server attributes them to the other party and a `fromMe`-only comparison
 * folds a whole legacy thread into one run under the wrong speaker. The envelope survives there.
 */
function speakerOf(message: ThreadSpeaker | undefined): string {
  const name = (message?.senderName ?? '').trim();
  if (name) return `name:${name.toLowerCase()}`;
  return message?.fromMe ? 'side:me' : 'side:them';
}

/**
 * The name to show above a run, or empty to show none.
 *
 * Prefers the envelope's own name; falls back to the two names a thread has — the viewer and the
 * contact — since an outreach has exactly two ends. Empty when neither is known, and the bubble's
 * side then carries the attribution on its own.
 */
export function senderLabel(message: ThreadSpeaker | undefined, names: { me: string; them: string; myAgent?: string; theirAgent?: string }): string {
  // An outreach is two ASSISTANTS corresponding, not two people typing: this side's Maestro wrote
  // the outbound messages and the other side's agent wrote the replies. So the agent's name is the
  // honest attribution, and the person's name is the fallback for the very common case where the
  // counterparty runs no assistant (they are not a Symphony user) — there the message really is
  // from them, relayed.
  //
  // The envelope is deliberately NOT preferred here any more. It names the person the outreach is
  // FROM ("Message from Amir Harel"), which is who it is on behalf of, not who composed it.
  const mine = messageIsMine(message, names.me);
  const agent = (mine ? names.myAgent : names.theirAgent)?.trim();
  if (agent) return agent;

  const envelope = (message?.senderName ?? '').trim();
  if (envelope) return envelope;
  return (mine ? names.me : names.them).trim();
}

/**
 * Which side of the transcript a message sits on.
 *
 * The envelope wins over `fromMe` for the same reason `speakerOf` prefers it — on a legacy message
 * `fromMe` is simply wrong, and a chat view puts every one of those on the wrong side. When the
 * envelope names the viewer, the message is theirs whatever the missing direction implies.
 */
export function messageIsMine(message: ThreadSpeaker | undefined, myName: string): boolean {
  const envelope = (message?.senderName ?? '').trim().toLowerCase();
  const mine = myName.trim().toLowerCase();
  if (envelope && mine) return envelope === mine;
  return Boolean(message?.fromMe);
}

/**
 * The label for a file attached to a message.
 *
 * The size is deliberately optional and usually absent — nothing in production sets it — so this
 * returns the name alone rather than "name · 0 B". Bytes are formatted only when a real size arrived.
 */
export function attachmentLabel(attachment: { fileName?: string; size?: number | string }): string {
  const name = (attachment.fileName ?? '').trim();
  const bytes = typeof attachment.size === 'string' ? Number(attachment.size) : attachment.size;
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return name;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${name} · ${rounded} ${units[unit]}`;
}

// ── The detail screen's thread groups ───────────────────────────────────────────────────────────

/** The subset of an outreach thread these rules read. */
export type ContactThread = {
  outreachId?: string;
  status?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
};

/** Which group a thread is filed under on the detail screen. */
export type ThreadGroup = 'active' | 'done';

/**
 * Which group a thread belongs to.
 *
 * Only an explicit `completed` is done. An outreach with **no** status is active, because an unset
 * status means "we do not know" — the server sends empty rather than guessing, and filing an
 * unknown under Done hides a live thread in the section nobody expands. Every outreach written
 * before the status field existed lands here, so this is the ordinary case on older threads rather
 * than an edge one.
 */
export function threadGroup(thread: ContactThread): ThreadGroup {
  return thread.status?.toLowerCase() === 'completed' ? 'done' : 'active';
}

/**
 * The detail screen's two groups, each newest-first.
 *
 * Ordered by when a thread last MOVED, while each group's header names when its threads STARTED —
 * the two dates answer different questions and the server sends both for exactly that reason.
 */
export function groupThreads(threads: ContactThread[]): {
  active: ContactThread[];
  done: ContactThread[];
} {
  const active: ContactThread[] = [];
  const done: ContactThread[] = [];
  // The caller hands us `outbound` concatenated with `inbound`, and the SAME outreach can be in
  // both: a self-outreach (the caller is its source AND its target — the weekly summaries Symphony
  // sends its own user) matches both of the server's pinned side queries, arriving twice with
  // `fromMe`/`waitingOnMe` inverted between the copies. Deduping here rather than at each of the
  // three call sites is what stops it rendering as two groups, once per speaker. The first copy
  // wins — `outbound` is passed first, and for an outreach the caller sent, "I said this" is the
  // reading that matches who wrote it.
  const seen = new Set<string>();
  for (const thread of threads) {
    const id = thread.outreachId ?? '';
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    (threadGroup(thread) === 'done' ? done : active).push(thread);
  }
  const byNewest = (a: ContactThread, b: ContactThread) => (toDate(b.updatedAt)?.getTime() ?? 0) - (toDate(a.updatedAt)?.getTime() ?? 0);
  active.sort(byNewest);
  done.sort(byNewest);
  return { active, done };
}

function toDate(at: string | Date | undefined): Date | undefined {
  if (!at) return undefined;
  const date = at instanceof Date ? at : new Date(at);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
